/**
 * Homepage hero picker → configurator.
 *
 * The hero's right-hand card is a compact "what are you making for?" picker:
 * a few furniture-type tiles, each showing a LIVE guide price and deep-linking
 * into the configurator pre-seeded via `?build=<token>` — the same rehydrate
 * path share links / saved designs use, so there's no parallel pricing logic and
 * it can never drift from the real engine.
 *
 * Rendered here (not in Liquid) so the guide prices are always the real ones:
 * each tile is built from `createOrder(starterId)` → `pricePiece` → a token.
 * Progressive enhancement: tiles are real <a href="?build=…"> links, so a click
 * works even if this handler never binds; the Liquid fallback link covers the
 * no-JS case. Loaded as a module; `#…` specifiers resolve via the importmap.
 */
import { CONFIG } from '#config';
import { createOrder } from '#model';
import { pricePiece } from '#pricing';
import { orderToToken } from '#serialize';
import { shapeById } from '#shapes';

const HEADER_OFFSET = 84; // clear the sticky header (~60px) + breathing room

/** Compact guide-price string for a starter's order, mirroring the
 *  configurator's own chooser. Empty if it can't be instant-priced. */
function guideFor(order) {
  if (order.pieces.some((p) => shapeById(p.shape).requiresTemplate)) return '';
  const units = order.pieces.map((p) => pricePiece(p).unitTotal).filter((u) => Number.isFinite(u) && u > 0);
  if (!units.length) return '';
  const min = Math.min(...units);
  const max = Math.max(...units);
  const span = min === max ? `£${min}` : `£${min}–£${max}`;
  const count = order.pieces.reduce((n, p) => n + p.quantity, 0);
  // Show the count so same-size sets (2- vs 3-seat) don't read as an identical
  // price. Per-cushion is deliberate: it keeps the low entry anchor (e.g. £70
  // dining) and defers the set total to the review — see the note to Tom.
  return count > 1 ? `Guide ${span}/cushion · ${count} in the set` : `Guide ${span}`;
}

/** The configurator's section wrapper (stable scroll target). */
function configuratorTarget() {
  return (
    document.getElementById('shopify-section-cushion_configurator') ||
    document.querySelector('cushion-configurator')?.closest('.shopify-section') ||
    document.querySelector('cushion-configurator')
  );
}

function scrollToConfigurator() {
  const el = configuratorTarget();
  if (!el) return false;
  const y = el.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;
  window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
  return true;
}

/** Build one starter tile as an <a> that carries a pre-seeded ?build= token. */
function tileFor(starter, builderUrl) {
  let token;
  let guide = '';
  try {
    const order = createOrder(starter.id);
    token = orderToToken(order);
    guide = guideFor(order);
  } catch {
    return ''; // a starter that won't seed/price is simply skipped
  }
  const base = builderUrl || location.pathname;
  const href = `${base}${base.includes('?') ? '&' : '?'}build=${token}`;
  return `
    <a class="herob__pick" href="${href}" data-starter="${starter.id}">
      <span class="herob__pick-main">
        <span class="herob__pick-label">${starter.label}</span>
        ${starter.blurb ? `<span class="herob__pick-blurb">${starter.blurb}</span>` : ''}
      </span>
      ${guide ? `<span class="herob__pick-price">${guide}</span>` : ''}
    </a>`;
}

function renderPicker(container) {
  const ids = (container.dataset.starters || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  const list = ids.length
    ? ids.map((id) => CONFIG.starters.find((s) => s.id === id)).filter(Boolean)
    : CONFIG.starters.slice(0, 4);
  const builderUrl = container.dataset.configuratorUrl || '';
  const tiles = list.map((s) => tileFor(s, builderUrl)).join('');
  if (tiles) container.innerHTML = tiles; // replaces the no-JS fallback link
}

function wireScrollLinks() {
  document.querySelectorAll('[data-hero-scroll]').forEach((link) => {
    link.addEventListener('click', (event) => {
      if (scrollToConfigurator()) event.preventDefault();
    });
  });
}

// After a tile opens the configurator via a same-page ?build= reload, glide down
// to it so the pre-seeded build is front and centre.
function maybeScrollOnLoad() {
  if (!new URLSearchParams(location.search).get('build')) return;
  if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
  requestAnimationFrame(() => setTimeout(scrollToConfigurator, 300));
}

document.querySelectorAll('[data-hero-picker]').forEach(renderPicker);
wireScrollLinks();
maybeScrollOnLoad();
